import { useMemo } from "react";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import FriendlyTime from "~/components/shared/FriendlyTime";
import Icon from "~/components/shared/Icon";
import { useNavigate } from "react-router-dom";
import { getDurationString, getDuration } from "~/lib/date";
import BktecBadge from "~/components/shared/BktecBadge";
import { urlForView } from "~/components/build/Show/lib/urlForView";
import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import {
  FilterByOption,
  useFilterStore,
  useStateCounts,
} from "~/components/build/Show/components/Sidebar/useFilterStore";
import Duration from "~/components/shared/Duration";
import { useSteps } from "~/components/build/Show/lib/useSteps";

// Map build states to display text
const buildStateDisplayMap: { [key: string]: string } = {
  scheduled: "scheduled",
  blocked: "blocked",
  running: "running",
  finished: "finished",
  failed: "failed",
  canceled: "canceled",
  canceling: "canceling",
  not_run: "not run",
  started: "running",
};

// Get a user-friendly cancellation status based on cancel_reason
function getCanceledStatusText(cancelReason: string | null | undefined): string {
  if (!cancelReason) {
    return "canceled";
  }

  switch (cancelReason) {
    case "user_canceled_via_ui":
      return "canceled by user";
    case "user_canceled_via_api":
      return "canceled via API";
    case "build_skipping":
      return "skipped by newer build";
    case "branch_deleted":
      return "canceled (branch deleted)";
    case "organization_locked":
      return "canceled (organization locked)";
    case "by_staff":
      return "canceled by staff";
    case "merge_group_destroyed":
      return "canceled (merge queue)";
    default:
      return "canceled";
  }
}

export default function ExecutionDetailsCard() {
  const { build } = useBuild();
  const setFilterBy = useFilterStore((state) => state.setFilterBy);
  const navigate = useNavigate();
  const counts = useStateCounts();
  const failedCount = counts["failed"];

  const handleOpenFailedSteps = () => {
    setFilterBy([FilterByOption.Failed]);
    navigate(urlForView(build.path, BuildView.Steps));
  };

  const handleOpenAllSteps = () => {
    setFilterBy([]);
    navigate(urlForView(build.path, BuildView.Canvas));
  };

  const handleOpenAllJobs = () => {
    setFilterBy([]);
    navigate(urlForView(build.path, BuildView.Steps));
  };

  let queueDurationStr: string | null = null;

  if (build.scheduledAt && build.startedAt) {
    const durationSeconds = getDuration(build.scheduledAt, build.startedAt).asSeconds();
    if (durationSeconds >= 0) {
      queueDurationStr = getDurationString(durationSeconds, "short");
    }
  }

  // --- Determine Queue Info ---
  const uniqueQueueKeys = useMemo(() => {
    return build.jobs
      ?.map((job) => job.clusterQueue?.key)
      .filter((key): key is string => !!key) // Filter out null/undefined keys and ensure type is string
      .reduce((acc, key) => {
        if (!acc.includes(key)) {
          acc.push(key);
        }
        return acc;
      }, [] as string[]);
  }, [build.jobs]);

  let queueDisplay: React.ReactNode = null;
  if (uniqueQueueKeys && uniqueQueueKeys.length === 1) {
    queueDisplay = <div className="truncate text-xs sm:text-sm">{uniqueQueueKeys[0]}</div>;
  } else if (uniqueQueueKeys && uniqueQueueKeys.length > 1) {
    queueDisplay = <div className="text-xs sm:text-sm">Multiple queues</div>;
  }
  // --- End Queue Info ---

  // --- Determine Agent Type Info ---
  const scriptJobs = build.jobs?.filter((job) => job.type === "script");
  const hasHosted = scriptJobs?.some((job) => job.hosted);
  const hasSelfHosted = scriptJobs?.some((job) => !job.hosted);

  let agentTypeDisplay: React.ReactNode = null;
  let agentTypeIcon: string | null = null;

  if (hasHosted && hasSelfHosted) {
    agentTypeDisplay = <div className="text-xs sm:text-sm">Mixed</div>;
    agentTypeIcon = "custom/outline/14x/agent"; // Generic icon for mixed
  } else if (hasHosted) {
    agentTypeDisplay = <div className="text-xs sm:text-sm">Hosted</div>;
    agentTypeIcon = "heroicons/outline/cloud";
  } else if (hasSelfHosted) {
    agentTypeDisplay = <div className="text-xs sm:text-sm">Self-hosted</div>;
    agentTypeIcon = "heroicons/outline/server-stack";
  }
  // --- End Agent Type Info ---

  const { steps, isLoading: isLoadingSteps } = useSteps();
  const totalSteps = useMemo(() => {
    return steps.filter((step) => step.type !== "group").length;
  }, [steps.length]);

  // Calculate total number of jobs run (script or trigger, not broken)
  const totalStartedJobs = useMemo(() => {
    const startedStates = ["running", "canceling", "finished", "failed", "canceled"];
    return build.jobs?.filter(
      (job) =>
        job.state !== "broken" &&
        (job.type === "script" || job.type === "trigger") &&
        startedStates.includes(job.state),
    ).length;
  }, [build.jobs]);

  return (
    <div
      className="shadow-depth-200 flex h-full flex-col rounded-md bg-white p-2 sm:p-4"
      data-testid="build-details-card"
    >
      <div className="mb-2 flex items-center justify-between">
        <h2 className="my-0 text-base font-semibold sm:text-lg">Execution details</h2>
      </div>

      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-3 text-xs sm:text-sm">
        <div className="min-w-[calc(50%-8px)]">
          <span className="mb-0.5 block text-gray-900">Status</span>
          <div className="flex items-center gap-1">
            <div className="text-xs capitalize sm:text-sm">
              {build.state === "canceled" || build.state === "canceling"
                ? getCanceledStatusText(build.cancelReason)
                : buildStateDisplayMap[build.state] || build.state}
            </div>
          </div>
        </div>

        {build.finishedAt && (
          <div className="min-w-[calc(50%-8px)]">
            <span className="mb-0.5 block text-gray-900">
              {build.state === "canceled" || build.state === "canceling" ? "Cancelled" : "Finished"}
            </span>
            <div className="flex items-center gap-1">
              <div className="text-xs sm:text-sm">
                <FriendlyTime value={build.finishedAt} capitalized={true} />
              </div>
            </div>
          </div>
        )}

        {/* Execution & Queued time */}
        {build.state !== "canceled" && build.state !== "canceling" && build.state !== "not_run" && (
          <div className="min-w-[calc(50%-8px)]">
            <span className="mb-0.5 block text-gray-900">Execution time</span>
            <div className="flex items-center gap-1 text-xs sm:text-sm">
              {/* Show Waiting if scheduled/blocked and not started, otherwise show duration/running */}
              {build.startedAt ? (
                // Only pass 'to' if build is finished, otherwise omit the prop
                build.finishedAt ? (
                  <Duration.Short
                    from={build.startedAt}
                    to={build.finishedAt}
                    updateFrequency={1000}
                  />
                ) : (
                  <Duration.Short from={build.startedAt} updateFrequency={1000} />
                )
              ) : build.state === "scheduled" || build.state === "blocked" ? (
                // Build has not started and is scheduled/blocked
                build.scheduledAt ? (
                  <>
                    Waiting <Duration.Short from={build.scheduledAt} updateFrequency={1000} />
                  </>
                ) : (
                  <>Waiting</> // Fallback in case scheduledAt is not available
                )
              ) : null}
            </div>
          </div>
        )}

        {queueDurationStr && (
          <div className="min-w-[calc(50%-8px)]">
            <span className="mb-0.5 block text-gray-900">Queued time</span>
            <div className="text-xs sm:text-sm">{queueDurationStr}</div>
          </div>
        )}

        {/* Divider */}
        <div className="w-full border-b" />

        {/* Combined Steps/Failed Steps and Jobs run */}
        <div className="flex w-full flex-wrap gap-x-4 gap-y-3">
          <div className="min-w-[calc(50%-8px)]">
            <span className="mb-0.5 block text-gray-900">Steps</span>
            <div className="flex items-center gap-1 text-xs sm:text-sm">
              {Features.ProgressiveBuildShow && isLoadingSteps ? (
                <div className="h-5 w-7 animate-pulse rounded-md bg-gray-200" />
              ) : (
                <button
                  onClick={handleOpenAllSteps}
                  className="text-charcoal-700 hover:text-charcoal-900 inline-flex border-none bg-transparent p-0 text-xs font-medium hover:underline sm:text-sm"
                >
                  {totalSteps ?? "N/A"}
                </button>
              )}
              {failedCount > 0 && (
                <div className="group flex items-center gap-0.5 rounded-md bg-red-100 px-2">
                  <button
                    onClick={handleOpenFailedSteps}
                    className="flex inline-flex items-center gap-0 border-none bg-transparent p-0 text-xs font-medium text-red-600 group-hover:text-red-800 group-hover:underline sm:text-sm"
                  >
                    {failedCount} Failed
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="min-w-[calc(50%-8px)]">
            <span className="mb-0.5 block text-gray-900">Jobs run</span>
            {Features.ProgressiveBuildShow && build.loading ? (
              <div className="h-5 w-7 animate-pulse rounded-md bg-gray-200" />
            ) : (
              <button
                onClick={handleOpenAllJobs}
                className="text-charcoal-700 hover:text-charcoal-900 inline-flex border-none bg-transparent p-0 text-xs font-medium hover:underline sm:text-sm"
              >
                {totalStartedJobs ?? "N/A"}
              </button>
            )}
          </div>
        </div>

        {/* Retries, Agent Type, and Queue */}
        <div className="flex w-full flex-wrap gap-x-4 gap-y-3">
          {build.retry_counts && build.retry_counts.total > 0 && (
            <div className="min-w-[calc(50%-8px)]">
              <span className="mb-0.5 block text-gray-900">Retries</span>
              <div className="text-xs sm:text-sm">
                {build.retry_counts.total}
                <span className="ml-1 text-xs font-normal text-gray-500">
                  ({build.retry_counts.manual} Manual, {build.retry_counts.automatic} Automatic)
                </span>
              </div>
            </div>
          )}
          {/* Environment Details */}
          {agentTypeDisplay && agentTypeIcon && (
            <div className="min-w-[calc(50%-8px)]">
              <span className="mb-0.5 block text-gray-900">Agent type</span>
              <div className="flex items-center gap-1 text-xs sm:text-sm">
                <Icon icon={agentTypeIcon} className="!m-0 h-4 w-4 text-gray-900" />
                {agentTypeDisplay}
              </div>
            </div>
          )}
          {queueDisplay && (
            <div className="min-w-[calc(50%-8px)]">
              <span className="mb-0.5 block text-gray-900">
                Queue{uniqueQueueKeys?.length > 1 ? "s" : ""}
              </span>
              <div className="flex items-center gap-1 text-xs sm:text-sm">
                <Icon icon="custom/outline/14x/queue" className="!m-0 h-4 w-4 text-gray-900" />
                {queueDisplay}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* BK Tech Badge section */}
      <div className="mt-3 border-t pt-3">
        {build.project.bktecBadgeEnabled && (
          <BktecBadge
            buildId={build.id}
            pipelineHasHistoricalTestPlans={build.project.hasTestPlans}
          />
        )}
      </div>
    </div>
  );
}
